import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaCogs, 
  FaHandshake, 
  FaChartLine, 
  FaLightbulb, 
  FaUsers, 
  FaRocket, 
  FaAward,
  FaArrowRight,
  FaCheckCircle,
  FaClipboardCheck,
  FaCode,
  FaTasks,
  FaEye,
  FaComments,
  FaStar,
  FaQuoteLeft,
  FaShieldAlt,
  FaClock
} from 'react-icons/fa';
import { pageSeoData, seoImage } from '../config/pageSeoConfig';
import './DevelopmentProcess.scss';

const processSteps = [
	{
		icon: FaLightbulb,
		title: 'Discovery & Strategy',
		step: '01',
		description: 'We dive deep to understand your business, goals, and audience. Every project starts with research, workshops, and a clear roadmap.',
		details: [
			'Business analysis & goal setting',
			'User research & persona development',
			'Competitive analysis',
			'Project roadmap & timeline'
		]
	},
	{
		icon: FaCogs,
		title: 'Design & Prototyping',
		step: '02',
		description: 'Our creative team crafts wireframes and prototypes, ensuring every interaction is intuitive and visually stunning.',
		details: [
			'Wireframing & user flow design',
			'High-fidelity UI/UX design',
			'Interactive prototyping',
			'Design system creation'
		]
	},
	{
		icon: FaRocket,
		title: 'Development & Testing',
		step: '03',
		description: 'We build robust, scalable solutions using the latest technologies. Rigorous QA ensures flawless performance on every device.',
		details: [
			'Agile development sprints',
			'Code reviews & quality assurance',
			'Cross-browser & device testing',
			'Performance optimization'
		]
	},
	{
		icon: FaHandshake,
		title: 'Launch & Growth',
		step: '04',
		description: 'We launch with precision, monitor results, and support your growth with ongoing optimization and transparent reporting.',
		details: [
			'Deployment & go-live support',
			'Performance monitoring',
			'Analytics & reporting',
			'Ongoing maintenance & updates'
		]
	}
];

const whyChooseUs = [
	{
		icon: FaEye,
		title: 'Full Visibility',
		description: 'Real-time updates, clear timelines, and no surprises. You\'re always in the loop with our transparent communication.'
	},
	{
		icon: FaComments,
		title: 'True Collaboration',
		description: 'We listen, adapt, and co-create with you at every step. Your feedback shapes the final product.'
	},
	{
		icon: FaCheckCircle,
		title: 'Quality Guaranteed',
		description: 'We own our results and measure success by your satisfaction. Excellence is our standard.'
	},
	{
		icon: FaChartLine,
		title: 'Measurable Results',
		description: 'Our solutions drive real business outcomes: more leads, higher conversions, and stronger brand presence.'
	},
	{
		icon: FaAward,
		title: 'Award-Winning Quality',
		description: 'We deliver excellence recognized by clients and industry leaders alike.'
	},
	{
		icon: FaUsers,
		title: 'Long-Term Partnerships',
		description: 'We build relationships, not just websites. Our clients trust us as their digital growth partners.'
	},
	{
		icon: FaShieldAlt,
		title: 'Security First',
		description: 'Enterprise-grade security practices, regular audits, and compliance with industry standards protect your data and users.'
	},
	{
		icon: FaClock,
		title: 'On-Time Delivery',
		description: 'We respect your deadlines and deliver projects on schedule without compromising quality or cutting corners.'
	}
];

const testimonials = [
	{
		text: "The transparency and collaboration throughout our project was exceptional. They kept us informed at every stage and delivered beyond our expectations.",
		author: "Michael Rodriguez",
		company: "Tech Innovations Inc.",
		rating: 5
	},
	{
		text: "Working with this team felt like having an extension of our own company. Their process is professional, efficient, and results-driven.",
		author: "Emily Thompson",
		company: "Growth Marketing Agency",
		rating: 5
	}
];

const WebDevelopmentProcess: React.FC = () => {
	// SEO constants from config
	const seoTitle = pageSeoData.DevelopmentProcess.title;
	const seoDescription = pageSeoData.DevelopmentProcess.description;
	const seoKeywords = pageSeoData.DevelopmentProcess.keywords;
	const seoUrl = pageSeoData.DevelopmentProcess.url;
	
	const schemaData = {
	  "@context": "https://schema.org",
	  "@graph": [
	    {
	      "@type": "Organization",
	      "@id": "https://appdeveloperpro.com/#organization",
	      "name": "App Developer Pro",
	      "url": "https://appdeveloperpro.com",
	      "logo": {
	        "@type": "ImageObject",
	        "url": "https://appdeveloperpro.com/images/logo.png",
	        "width": 300,
	        "height": 100
	      },
	      "sameAs": [
	        "https://facebook.com/appdeveloperpro",
	        "https://twitter.com/appdeveloperpro",
	        "https://linkedin.com/company/appdeveloperpro",
	        "https://instagram.com/appdeveloperpro"
	      ],
	      "description": "Award-winning web development company specializing in custom web applications, digital transformation, and business growth.",
	      "contactPoint": {
	        "@type": "ContactPoint",
	        "telephone": "+91-(882)-636-0188",
	        "contactType": "customer service",
	        "email": "info@appdeveloperpro.com",
	        "availableLanguage": ["English"]
	      }
	    },
	    {
	      "@type": "WebPage",
	      "@id": "https://appdeveloperpro.com/development-process#webpage",
	      "url": seoUrl,
	      "name": seoTitle,
	      "description": seoDescription,
	      "inLanguage": "en-US",
	      "isPartOf": {
	        "@id": "https://appdeveloperpro.com/#website"
	      },
	      "about": {
	        "@id": "https://appdeveloperpro.com/#organization"
	      },
	      "primaryImageOfPage": {
	        "@type": "ImageObject",
	        "url": seoImage
	      },
	      "datePublished": "2019-01-01T00:00:00+00:00",
	      "dateModified": "2025-11-03T00:00:00+00:00"
	    },
	    {
	      "@type": "BreadcrumbList",
	      "itemListElement": [
	        {
	          "@type": "ListItem",
	          "position": 1,
	          "name": "Home",
	          "item": "https://appdeveloperpro.com"
	        },
	        {
	          "@type": "ListItem",
	          "position": 2,
	          "name": "How We Work",
	          "item": seoUrl
	        }
	      ]
	    }
	  ]
	};
	
	return (
		<>
			<Helmet>
				<title>{seoTitle}</title>
				<meta name="description" content={seoDescription} />
				<meta name="keywords" content={seoKeywords} />
				<meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
				<meta name="author" content="App Developer Pro" />
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />
				<meta name="theme-color" content="#3b82f6" />
				<link rel="canonical" href={seoUrl} />
				{/* Open Graph */}
				<meta property="og:title" content={seoTitle} />
				<meta property="og:description" content={seoDescription} />
				<meta property="og:type" content="website" />
				<meta property="og:url" content={seoUrl} />
				<meta property="og:site_name" content="App Developer Pro" />
				<meta property="og:image" content={seoImage} />
				<meta property="og:image:width" content="1200" />
				<meta property="og:image:height" content="630" />
				<meta property="og:image:alt" content="How We Work - App Developer Pro" />
				<meta property="og:locale" content="en_US" />
				{/* Twitter Card */}
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:site" content="@appdeveloperpro" />
				<meta name="twitter:creator" content="@appdeveloperpro" />
				<meta name="twitter:title" content={seoTitle} />
				<meta name="twitter:description" content={seoDescription} />
				<meta name="twitter:image" content={seoImage} />
				<meta name="twitter:image:alt" content="How We Work - App Developer Pro" />
				{/* Schema.org Markup */}
				<script type="application/ld+json">{JSON.stringify(schemaData)}</script>
			</Helmet>
			
			<div className="web-development">
				{/* Hero Section */}
				<section className="web-development__hero">
					<div className="web-development__hero-content">
						<motion.div
							className="web-development__hero-text"
							initial={{ opacity: 0, y: 50 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8 }}
						>
							<h1 className="web-development__hero-title">
								Proven Web & Mobile Application Development Process for Success
							</h1>
							<p className="web-development__hero-subtitle">
								Experience a transparent, collaborative, and results-driven journey from idea to launch—and beyond. Full Stack Expert Pro for web & mobile application development services, partners with you at every stage to ensure exceptional outcomes.
							</p>
							<div className="web-development__hero-buttons">
								<Link to="/contact" className="web-development__hero-btn web-development__hero-btn--primary">
									Start Your Project <FaArrowRight />
								</Link>
								<Link to="/portfolio" className="web-development__hero-btn web-development__hero-btn--secondary">
									View Our Work
								</Link>
							</div>
						</motion.div>
						<motion.div
							className="web-development__hero-visual"
							initial={{ opacity: 0, x: 50 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
						>
							<div className="web-development__hero-workflow">
								<div className="workflow-steps-row">
									<div className="workflow-step">
										<div className="workflow-icon">
											<FaLightbulb />
										</div>
										<span>Discovery</span>
									</div>
									<div className="workflow-arrow">
										<FaArrowRight />
									</div>
									<div className="workflow-step">
										<div className="workflow-icon">
											<FaCogs />
										</div>
										<span>Design</span>
									</div>
									<div className="workflow-arrow">
										<FaArrowRight />
									</div>
									<div className="workflow-step">
										<div className="workflow-icon">
											<FaRocket />
										</div>
										<span>Develop</span>
									</div>
									<div className="workflow-arrow">
										<FaArrowRight />
									</div>
									<div className="workflow-step">
										<div className="workflow-icon">
											<FaHandshake />
										</div>
										<span>Launch</span>
									</div>
								</div>
								<div className="workflow-stats">
									<div className="stat-card">
										<FaCheckCircle />
										<div className="stat-info">
											<strong>100%</strong>
											<span>Transparency</span>
										</div>
									</div>
									<div className="stat-card">
										<FaUsers />
										<div className="stat-info">
											<strong>24/7</strong>
											<span>Support</span>
										</div>
									</div>
									<div className="stat-card">
										<FaAward />
										<div className="stat-info">
											<strong>500+</strong>
											<span>Projects</span>
										</div>
									</div>
								</div>
							</div>
						</motion.div>
					</div>
				</section>

				{/* Process Steps Section */}
				<section className="web-development__features">
					<div className="web-development__container">
						<motion.div
							className="web-development__section-header"
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
							viewport={{ once: true }}
						>
							<h2>Web & Mobile Application Development Process</h2>
							<h3>Full Stack Expert's Proven Process</h3>
							<p>App Developer Pro follow the step-by-step approach to building exceptional web & mobile application development solutions.</p>
						</motion.div>
						
						<div className="web-development__process-grid">
							{processSteps.map((step, index) => (
								<motion.div
									key={step.title}
									className="web-development__process-card"
									initial={{ opacity: 0, y: 30 }}
									whileInView={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.5, delay: index * 0.1 }}
									viewport={{ once: true }}
								>
																	<div className="web-development__process-step">{step.step}</div>
										<div className="web-development__process-header">
											<div className="web-development__process-icon">
												{React.createElement(step.icon)}
											</div>
											<h3>{step.title}</h3>
										</div>
									<p>{step.description}</p>
									<ul className="web-development__process-details">
										{step.details.map((detail, idx) => (
											<li key={idx}>
												<FaCheckCircle /> {detail}
											</li>
										))}
									</ul>
								</motion.div>
							))}
						</div>
					</div>
				</section>

				{/* Why Choose Us Section */}
				<section className="web-development__benefits" style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #1E3A5F 100%)' }}>
					<div className="web-development__container">
						<motion.div
							className="web-development__section-header"
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
							viewport={{ once: true }}
						>
							<h2 style={{ color: '#fff' }}>Why Partner With Us?</h2>
							<p style={{ color: 'rgba(255,255,255,0.9)' }}>We believe in radical transparency, open communication, and a relentless focus on your goals</p>
						</motion.div>
						
						<div className="web-development__benefits-grid">
							{whyChooseUs.map((item, index) => (
								<motion.div
									key={item.title}
									className="web-development__benefit-card"
									initial={{ opacity: 0, scale: 0.9 }}
									whileInView={{ opacity: 1, scale: 1 }}
									transition={{ duration: 0.5, delay: index * 0.1 }}
									viewport={{ once: true }}
								>
									<div className="web-development__benefit-header">
										<div className="web-development__benefit-icon">
											{React.createElement(item.icon)}
										</div>
										<h3>{item.title}</h3>
									</div>
									<p>{item.description}</p>
								</motion.div>
							))}
						</div>
					</div>
				</section>

				{/* Testimonials Section */}
				<section className="web-development__testimonials">
					<div className="web-development__container">
						<motion.div
							className="web-development__section-header"
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
							viewport={{ once: true }}
						>
							<h2>What Our Clients Say</h2>
							<p>Real feedback from real partnerships</p>
						</motion.div>
						
						<div className="web-development__testimonials-grid">
							{testimonials.map((testimonial, index) => (
								<motion.div
									key={index}
									className="web-development__testimonial-card"
									initial={{ opacity: 0, y: 30 }}
									whileInView={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.5, delay: index * 0.2 }}
									viewport={{ once: true }}
								>
									<FaQuoteLeft className="web-development__testimonial-quote" />
									<p className="web-development__testimonial-text">{testimonial.text}</p>
									<div className="web-development__testimonial-rating">
										{[...Array(testimonial.rating)].map((_, i) => (
											<FaStar key={i} />
										))}
									</div>
									<div className="web-development__testimonial-author">
										<strong>{testimonial.author}</strong>
										<span>{testimonial.company}</span>
									</div>
								</motion.div>
							))}
						</div>
					</div>
				</section>

				{/* FAQ Section */}
				<section className="web-development__faq">
				<motion.div
					className="web-development__section-header"
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
				>
					<h2>Frequently Asked Questions About Our Development Process</h2>
					<p>Everything you need to know about working with App Developer Pro</p>
				</motion.div>
				
				<div className="web-development__faq-list">
							<motion.div
								className="web-development__faq-item"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5 }}
								viewport={{ once: true }}
							>
								<h3>How transparent is your development process?</h3>
								<p>We believe in radical transparency. You'll have access to real-time project dashboards, regular sprint demos, daily stand-up summaries, and direct communication with your development team. We use tools like Jira, Slack, and GitHub to keep you informed at every stage.</p>
							</motion.div>
							
							<motion.div
								className="web-development__faq-item"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.1 }}
								viewport={{ once: true }}
							>
								<h3>What happens if my requirements change mid-project?</h3>
								<p>Our agile methodology is designed for change. We work in 2-week sprints, allowing flexibility to adjust priorities, add features, or pivot direction based on market feedback or business needs. We'll discuss the impact on timeline and budget transparently before implementing changes.</p>
							</motion.div>
							
							<motion.div
								className="web-development__faq-item"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.2 }}
								viewport={{ once: true }}
							>
								<h3>How do you ensure the final product meets my expectations?</h3>
								<p>We involve you throughout the process: discovery workshops to capture your vision, design mockups and prototypes for your approval, regular demo sessions to gather feedback, and comprehensive UAT (User Acceptance Testing) before launch. Your satisfaction is our success metric.</p>
							</motion.div>
							
							<motion.div
								className="web-development__faq-item"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.3 }}
								viewport={{ once: true }}
							>
								<h3>What support do you provide after launch?</h3>
								<p>We offer comprehensive post-launch support including bug fixes, performance monitoring, security updates, feature enhancements, and 24/7 technical support. We view our relationship as a long-term partnership, helping you grow and scale your digital solutions over time.</p>
							</motion.div>
							
							<motion.div
								className="web-development__faq-item"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.4 }}
								viewport={{ once: true }}
							>
								<h3>How do you handle project communication and collaboration?</h3>
								<p>We assign a dedicated project manager as your single point of contact. Communication happens via your preferred channels (Slack, email, video calls). You'll receive weekly progress reports, monthly strategy reviews, and have direct access to team members for technical discussions.</p>
							</motion.div>
							
							<motion.div
								className="web-development__faq-item"
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.5 }}
								viewport={{ once: true }}
							>
								<h3>What industries do you have experience working with?</h3>
								<p>We've successfully delivered projects across diverse industries including e-commerce, healthcare, fintech, education, real estate, SaaS, and more. Our process adapts to your industry's specific requirements, compliance needs, and best practices while bringing fresh perspectives from cross-industry expertise.</p>
							</motion.div>
						</div>

				</section>

				{/* CTA Section */}
				<section className="web-development__cta">
					<div className="web-development__container">
						<motion.div
							className="web-development__cta-content"
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
							viewport={{ once: true }}
						>
							<h2>Ready to Transform Your Digital Presence?</h2>
							<p>Let's build something extraordinary together. Contact us today to start your journey.</p>
							<Link to="/contact" className="web-development__cta-btn">
								Get Started Now <FaArrowRight />
							</Link>
						</motion.div>
					</div>
				</section>
			</div>
		</>
	);
};

export default WebDevelopmentProcess;

